import React from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Brain,
  Sparkles,
  Zap,
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  TrendingUp,
  Shield,
  Rocket,
  Target,
  Globe,
  Award,
  BarChart3,
  GitBranch,
  Calendar,
  Download,
  Search,
  Layers,
  Play,
  ChevronRight,
  Lightbulb,
  Briefcase,
  Route,
  Compass,
  FileText,
  Lock
} from 'lucide-react';

const Homepage = () => {
  const features = [
    {
      icon: Sparkles,
      title: "Ideation",
      description: "Generate breakthrough ideas with AI-powered creative thinking",
      color: "sqsft-gradient-primary",
      badge: "AI",
      path: "/ideation",
      locked: true
    },
    {
      icon: Target,
      title: "Problem Solving",
      description: "Systematic innovation using proven inventive principles",
      color: "sqsft-gradient-primary",
      badge: null,
      path: "/problem-solving",
      locked: true
    },
    {
      icon: Route,
      title: "Product Roadmap Innovation",
      description: "AI-powered planning with strategic innovation paths",
      color: "sqsft-gradient-primary",
      badge: null,
      path: "/roadmap-innovation",
      locked: false,
      isMain: true
    },
    {
      icon: Compass,
      title: "New Market and Product Discovery",
      description: "Find opportunities in untapped market segments",
      color: "sqsft-gradient-gold",
      badge: "New",
      path: "/market-discovery",
      locked: true
    },
    {
      icon: Shield,
      title: "IP Creation and Patent Strategy",
      description: "Patent development and intellectual property protection",
      color: "sqsft-gradient-gold",
      badge: null,
      path: "/ip-strategy",
      locked: true
    },
    {
      icon: Briefcase,
      title: "Business Model Innovation",
      description: "Innovation frameworks for business transformation",
      color: "sqsft-gradient-primary",
      badge: null,
      path: "/business-model",
      locked: true
    },
    {
      icon: Building2,
      title: "Algorithm Optimization",
      description: "Optimize your algorithms for maximum efficiency",
      color: "sqsft-gradient-primary",
      badge: "Beta",
      path: "/algorithm-optimization",
      locked: true
    }
  ];

  const stats = [
    { value: "10x", label: "Faster Innovation" },
    { value: "AI", label: "Powered Platform" },
    { value: "7+", label: "Innovation Tools" },
    { value: "Beta", label: "Early Access" }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgb(var(--color-border))' }}>
        <div className="max-w-6xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/home" className="flex items-center space-x-3">
              <div className="w-12 h-12 sqsft-gradient-primary rounded-xl flex items-center justify-center">
                <Rocket className="w-7 h-7 text-white" />
              </div>
              <span className="text-4xl font-bold sqsft-heading-1"> SQUARESHIFT</span>
            </Link>
            
            <Link to="/roadmap-innovation" className="sqsft-btn-primary">
              Contact Us
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-8">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-12">
            <h1 className="text-6xl lg:text-7xl sqsft-heading-1 mb-8 leading-tight">
              AI-Powered
              <br />
              <span className="sqsft-gradient-text">Innovation Platform</span>
            </h1>
            
            <p className="text-xl sqsft-body mb-12 max-w-2xl mx-auto" style={{ color: 'rgb(var(--color-text-secondary))' }}>
              Transform ideas into breakthrough products with AI-driven innovation tools
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
                              <Link to="/roadmap-innovation" className="sqsft-btn-primary text-lg px-8 py-4 flex items-center space-x-2">
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold sqsft-gradient-text mb-2">{stat.value}</div>
                <div className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 px-8 sqsft-gradient-subtle">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <div className="inline-flex items-center px-4 py-2 sqsft-badge sqsft-badge-primary rounded-full mb-8">
              <Zap className="w-4 h-4 mr-2" />
              Complete Innovation Platform
            </div>
            <h2 className="text-5xl lg:text-6xl sqsft-heading-1 mb-8 leading-tight">
              Innovation Made
              <br />
              <span className="sqsft-gradient-text">Simple</span>
            </h2>
            <p className="text-xl sqsft-body max-w-2xl mx-auto" style={{ color: 'rgb(var(--color-text-secondary))' }}>
              Seven powerful AI tools to accelerate your innovation journey from ideation to market
            </p>
          </div>

          {/* Main Features Grid */}
          <div className="mb-16">
            {/* Main Feature - Product AI Roadmap */}
            {features.filter(f => f.isMain).map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="mb-12">
                  <div className="group relative">
                    {/* Enhanced colored shadow backdrop */}
                    <div className={`absolute -inset-2 ${feature.color} rounded-3xl opacity-0 group-hover:opacity-40 blur-2xl transition-all duration-500 group-hover:blur-3xl`}></div>
                    
                    {/* Main card */}
                    <div className="relative sqsft-feature-card p-12 border-2 transition-all duration-300 group-hover:border-transparent group-hover:shadow-2xl group-hover:scale-[1.01] group-hover:-translate-y-1" style={{ borderColor: 'rgb(var(--color-sage-green))' }}>
                      <div className="flex flex-col lg:flex-row items-center space-y-8 lg:space-y-0 lg:space-x-12">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <div className={`w-24 h-24 ${feature.color} rounded-3xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300`}>
                              <Icon className="w-12 h-12 text-white" />
                            </div>
                            <div className={`absolute -inset-3 ${feature.color} rounded-3xl opacity-20 blur-2xl group-hover:opacity-50 transition-opacity duration-300`}></div>
                          </div>
                        </div>
                        
                        <div className="flex-1 text-center lg:text-left">
                          <div className="flex items-center justify-center lg:justify-start space-x-3 mb-4">
                            <h3 className="text-3xl sqsft-heading-2">
                              {feature.title}
                            </h3>
                            <span className="px-3 py-1 sqsft-gradient-gold text-white text-sm font-medium rounded-full">
                              Available Now
                            </span>
                          </div>
                          
                          <p className="text-xl sqsft-body mb-8 leading-relaxed" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            {feature.description}
                          </p>
                          
                          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-6">
                            <Link to={feature.path} className="inline-flex items-center px-8 py-4 sqsft-gradient-primary text-white font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 group">
                              <span>Get Started</span>
                              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/animated-workflow" className="font-medium flex items-center space-x-2 group" style={{ color: 'rgb(var(--color-deep-green))' }}>
                              <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                              <span>See How It Works</span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Locked Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.filter(f => !f.isMain).map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="group relative">
                    {/* Colored shadow backdrop */}
                    <div className={`absolute -inset-1 ${feature.color} rounded-2xl opacity-0 group-hover:opacity-30 blur-xl transition-all duration-500 group-hover:blur-2xl`}></div>
                    
                    {/* Card */}
                    <div className="relative sqsft-card p-8 border transition-all duration-300 group-hover:border-transparent group-hover:shadow-2xl group-hover:-translate-y-1">
                      {/* Lock badge */}
                      <div className="absolute top-4 right-4 z-10">
                        <div className="rounded-full p-2" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}>
                          <Lock className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mb-6">
                        <div className="relative">
                          <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                            <Icon className="w-8 h-8 text-white" />
                          </div>
                          <div className={`absolute -inset-2 ${feature.color} rounded-2xl opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-300`}></div>
                        </div>
                        {feature.badge && (
                          <span className={`sqsft-badge ${
                            feature.badge === 'AI' 
                              ? 'sqsft-badge-primary'
                              : feature.badge === 'New'
                              ? 'sqsft-badge-success'
                              : feature.badge === 'Beta'
                              ? 'sqsft-badge-primary'
                              : 'sqsft-badge-primary'
                          }`}>
                            {feature.badge}
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-2xl sqsft-heading-2 mb-4 transition-all duration-300">
                        {feature.title}
                      </h3>
                      
                      <p className="sqsft-body mb-6 leading-relaxed" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        {feature.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-sm sqsft-small flex items-center" style={{ color: 'rgb(var(--color-text-muted))' }}>
                          <Lock className="w-4 h-4 mr-1" />
                          Coming Soon
                        </div>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                          <ChevronRight className="w-5 h-5" style={{ color: 'rgb(var(--color-text-muted))' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <div className="inline-flex items-center space-x-6">
              <Link to="/roadmap-innovation" className="px-8 py-4 sqsft-gradient-primary text-white font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl">
                Start Your Innovation Journey
              </Link>
              <Link to="/animated-workflow" className="px-8 py-4 font-semibold transition-colors duration-300 flex items-center space-x-2 group" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: 'rgba(66, 102, 83, 0.1)' }}>
                  <Play className="w-4 h-4" style={{ color: 'rgb(var(--color-deep-green))' }} />
                </div>
                <span>Watch Demo</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 sqsft-gradient-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sqsft-heading-1 text-white mb-8">
            Start Your Innovation Journey
            </h2>
            
          <p className="text-xl sqsft-body mb-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Transform ideas into breakthrough products with AI innovation tools
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link to="/roadmap-innovation" className="px-8 py-4 sqsft-btn-secondary text-lg">
              Start Free Trial
              </Link>
            <Link to="/market-discovery" className="px-8 py-4 sqsft-btn-primary text-lg">
              Schedule Demo
              </Link>
            </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-8" style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}>
        {/* Contact Form Section */}
        <div className="max-w-7xl mx-auto mb-16">
          <div className="text-center mb-12">
            <h3 className="text-white text-lg font-light mb-2">Need More Details?</h3>
            <h2 className="text-white text-4xl font-bold">Contact Us</h2>
          </div>
          
          <form className="flex flex-col lg:flex-row items-end justify-center gap-4 max-w-6xl mx-auto">
            <input
              type="text"
              placeholder="Name"
              className="flex-1 px-6 py-4 rounded-lg border-none outline-none text-gray-900 placeholder-gray-500"
            />
            <input
              type="email"
              placeholder="Business Email"
              className="flex-1 px-6 py-4 rounded-lg border-none outline-none text-gray-900 placeholder-gray-500"
            />
            <input
              type="tel"
              placeholder="Mobile number (with country code)"
              className="flex-1 px-6 py-4 rounded-lg border-none outline-none text-gray-900 placeholder-gray-500"
            />
            <button
              type="submit"
              className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
            >
              SUBMIT
            </button>
          </form>
        </div>

        {/* Footer Navigation */}
        <div className="max-w-7xl mx-auto" style={{ backgroundColor: 'rgb(var(--color-deep-green))' }}>
          <div className="px-8 py-16">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Company Info */}
              <div className="lg:col-span-1">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 sqsft-gradient-primary rounded-lg flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-white">SQUARESHIFT</span>
                </div>
                
                <p className="text-gray-300 mb-8 leading-relaxed">
                  SquareShift helps businesses redefine success with innovative Cloud, Data, and AI solutions
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center">
                      <span className="text-white text-sm">@</span>
                    </div>
                    <span className="text-gray-300">sales@squareshift.co</span>
                  </div>
                  
                  <div className="flex space-x-4">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer">
                      <span className="text-blue-600 font-bold">in</span>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer">
                      <span className="text-gray-900 font-bold">X</span>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer">
                      <span className="text-pink-600 font-bold">ig</span>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer">
                      <span className="text-red-600 font-bold">yt</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Industries */}
              <div>
                <h4 className="text-white font-semibold mb-6">Industries</h4>
                <ul className="space-y-3">
                  <li><Link to="/retail" className="text-gray-300 hover:text-white transition-colors">Retail</Link></li>
                  <li><Link to="/hi-tech" className="text-gray-300 hover:text-white transition-colors">Hi-Tech</Link></li>
                  <li><Link to="/banking" className="text-gray-300 hover:text-white transition-colors">Banking and Financial Services</Link></li>
                </ul>
              </div>

              {/* Solutions */}
              <div>
                <h4 className="text-white font-semibold mb-6">Solutions</h4>
                <ul className="space-y-3">
                  <li><Link to="/google-agentspace" className="text-gray-300 hover:text-white transition-colors">Google Agentspace</Link></li>
                  <li><Link to="/data" className="text-gray-300 hover:text-white transition-colors">Data</Link></li>
                  <li><Link to="/digital" className="text-gray-300 hover:text-white transition-colors">Digital</Link></li>
                  <li><Link to="/elastic-solutions" className="text-gray-300 hover:text-white transition-colors">Elastic Solutions</Link></li>
                  <li><Link to="/ai-ml" className="text-gray-300 hover:text-white transition-colors">AI & ML</Link></li>
                </ul>
              </div>

              {/* Case Studies */}
              <div>
                <h4 className="text-white font-semibold mb-6">Case Studies</h4>
                <ul className="space-y-3">
                  <li><Link to="/elastic-case-studies" className="text-gray-300 hover:text-white transition-colors">Elastic case studies</Link></li>
                  <li><Link to="/bi-data-case-studies" className="text-gray-300 hover:text-white transition-colors">BI & Data case studies</Link></li>
                </ul>
              </div>

              {/* Insights & Company */}
              <div>
                <div className="mb-8">
                  <h4 className="text-white font-semibold mb-6">Insights</h4>
                  <ul className="space-y-3">
                    <li><Link to="/blogs" className="text-gray-300 hover:text-white transition-colors">Blogs</Link></li>
                    <li><Link to="/webinar" className="text-gray-300 hover:text-white transition-colors">Webinar</Link></li>
                    <li><Link to="/ebooks" className="text-gray-300 hover:text-white transition-colors">E-Book & Brouchers</Link></li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-white font-semibold mb-6">Company</h4>
                  <ul className="space-y-3">
                    <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
                    <li><Link to="/careers" className="text-gray-300 hover:text-white transition-colors">Careers</Link></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;